import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../Api";
import { 
  UserIcon, 
  InformationCircleIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowDownTrayIcon, 
  PlusIcon, 
  ArrowRightIcon,
  ExclamationTriangleIcon
} from "@heroicons/react/24/outline";

const OnboardingAgreement: React.FC = () => {
  const { user } = useAuth0();
  const api = useApi();
  const navigate = useNavigate();
  const [canImportDWPData, setCanImportDWPData] = useState(false);
  const [alreadyImportedDWPData, setAlreadyImportedDWPData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const checkUserExistsInOnboarding = async (email?: string) => {
      if (!email) return;
      setLoading(true);
      try {
        const response = await api.get("/api/onboarding/CheckCanImportDWPData");
        // If user is already onboarded, redirect to dashboard
        if (response.data && response.data.alreadyImported) {
          setCanImportDWPData(false);
          setAlreadyImportedDWPData(true);
        } else if (response.data && response.data.dwpDataExists) {
          setCanImportDWPData(true);
          setAlreadyImportedDWPData(false);
        } else {
          setCanImportDWPData(false);
          setAlreadyImportedDWPData(false);
        }
      } catch (error: any) {
        navigate("/error", {
          state: {
            error: error?.response?.data || "An error occurred while checking the user"
          }
        });
        return;
      }
      setLoading(false);
    };
    checkUserExistsInOnboarding(user?.email);
  }, [user?.email, api, navigate]);

  const handleImportDWPData = async () => {
    setProcessing(true);
    setImportError(null);
    try {
      await api.post("/api/onboarding/ImportDWPData");
      setImportSuccess(true);
      setProcessing(false);
      // Automatically navigate to individual page after successful import so user can review/edit data
      setTimeout(() => {
        navigate("/onboarding/individual");
      }, 1500); // Give user time to see the success message
    } catch (error: any) {
      setImportError(error?.response?.data || "Failed to import existing data. Please try again.");
      setProcessing(false);
    }
  };

  const handleSkipImportStep = async () => {
    setProcessing(true);
    setImportError(null);
    try {
      await api.post("/api/onboarding/MarkImportComplete");
      setShowConfirmModal(false);
      navigate("/onboarding/individual");
    } catch (error: any) {
      setImportError("Failed to navigate. Please try again.");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Loading</h3>
            <p className="text-sm text-gray-500">Checking your account status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-width-breakout min-h-[calc(100vh-200px)] flex items-center justify-center">
      <div className="max-w-lg mx-auto w-full px-4 relative z-10">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-10 py-12">
            {/* Header */}
            <div className="text-center">
               <div className="inline-flex items-center justify-center w-16 h-16 dfo-bg-gradient-green-static rounded-2xl mb-6 shadow-lg">
                 <UserIcon className="h-8 w-8 text-white" />
               </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">User Onboarding</h1>
              <p className="text-gray-600 text-base mb-4">We need to collect some information in order to setup your account:</p>
              
              {/* Email Display */}
              <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  {user?.email}
                </span>
              </div>
              
              {/* Green Horizontal Line */}
              <div className="w-64 h-1 dfo-bg-gradient-green-static rounded-full mx-auto mb-4"></div>
              
              {/* Info Icon and Collapsible Section */}
              <div className="mb-4">
                <div className="inline-flex items-center">
                  <span className="dfo-text-info">
                    {canImportDWPData ? "DWP Import Status:" : "Onboarding Information"}
                  </span>
                  {!canImportDWPData && (
                     <button
                       onClick={() => setIsInfoExpanded(!isInfoExpanded)}
                       className="hover:bg-[#b38746]/20 text-[#b38746] transition-colors p-1 rounded-full ml-2"
                       title="Click to toggle Onboarding Information details"
                     >
                       <InformationCircleIcon className="h-5 w-5" />
                     </button>
                  )}
                </div>
              </div>
            </div>
            {/* Status Messages */}
            {importSuccess ? (
              <div className="alert-success">
                 <div className="flex items-start">
                   <div className="flex-shrink-0">
                     <CheckCircleIcon className="h-5 w-5 text-green-600" />
                   </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">Data imported successfully</h3>
                    <p className="text-sm text-green-700 mt-1">Your existing profile has been imported and you can now continue.</p>
                  </div>
                </div>
              </div>
            ) : importError ? (
              <div className="alert-danger">
                 <div className="flex items-start">
                   <div className="flex-shrink-0">
                     <XCircleIcon className="h-5 w-5 text-red-400" />
                   </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Import error</h3>
                    <p className="text-sm text-red-700 mt-1">{importError}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Collapsible Main Content */}
            <div className={canImportDWPData ? '' : `collapsible-content ${isInfoExpanded ? 'expanded' : ''}`}>
              <div className="mb-6">
                <div className="text-left-align">
                  <div className="bg-gray-50 rounded-xl p-6 mb-4">
                    {canImportDWPData ? (
                      <>
                        <p className="dfo-text-body">
                          We found an existing profile for you in Digital Wealth Partners using your login email.
                        </p>
                        <p className="dfo-text-body">
                          You can import your existing data to get started quickly, or start fresh with a new profile.
                        </p>
                      </>
                    ) : alreadyImportedDWPData ? (
                      <>
                        <p className="dfo-text-body">
                          You have already imported your Digital Wealth Partners profile using your login email.
                        </p>
                        <p className="dfo-text-body">
                          You can continue with your existing profile or contact support if you need assistance.
                        </p>
                      </>
                    ) : (
                          <>
                            <p className="dfo-text-body">
                              During the onboarding process, you'll be asked to provide information about yourself, your businesses, trusts, and other financial details.
                            </p>
                            <p className="dfo-text-body">
                              This information will be used to set up your account and ensure we have all the necessary details to provide you with the best service.
                            </p>
                          </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {canImportDWPData && !importSuccess ? (
                <>
                  <button
                    onClick={handleImportDWPData}
                    disabled={processing}
                    className="btn-primary-lg w-3/4 mx-auto"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-1 border-white border-t-transparent mr-3"></div>
                        Importing...
                      </>
                     ) : (
                       <>
                         <ArrowDownTrayIcon className="h-5 w-5 mr-3" />
                         Import Data
                       </>
                     )}
                  </button>
                      <button
                        onClick={() => setShowConfirmModal(true)}
                        disabled={processing}
                        className="btn-secondary w-3/4 mx-auto"
                      >
                     <PlusIcon className="h-5 w-5 mr-3" />
                     Start Fresh
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSkipImportStep}
                  disabled={processing}
                  className="btn-primary-lg w-3/4 mx-auto"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-1 border-white border-t-transparent mr-3"></div>
                      Processing...
                    </>
                   ) : (
                     <>
                       Continue
                       <ArrowRightIcon className="h-5 w-5 ml-3" />
                     </>
                   )}
                </button>
              )}
            </div>

            {/* Support */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <p className="dfo-text-muted text-center">
                Need help? <a className="link" href="mailto:support@digitalfamilyoffice.io">Contact our support team</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="text-center">
               <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-4">
                 <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
               </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Fresh?</h3>
              <p className="text-gray-600 text-sm mb-6">
                You're about to start with a new profile. This will mark your DWP data import as complete, and you won't be able to go back and import your existing data later.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="btn-secondary flex-1 py-2 px-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSkipImportStep}
                  disabled={processing}
                  className="btn-primary-lg flex-1 py-2 px-4"
                >
                  {processing ? "Processing..." : "Start Fresh"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingAgreement;